import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

/**
 * Component that automatically redirects users based on their role after authentication
 */
const RoleBasedRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile, isAuthenticated, isLoading } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't redirect if still loading
    if (isLoading) return;

    // Only redirect authenticated users
    if (isAuthenticated && user && profile?.role) {
      const currentPath = window.location.pathname;
      
      // Check user role and redirect accordingly
      switch (profile.role) {
        case 'business':
          // Redirect business users to seller dashboard if not already on business routes
          if (!currentPath.startsWith('/business/')) {
            console.log('Business user detected, redirecting to business dashboard');
            navigate('/business/seller-dashboard', { replace: true });
          }
          break;
          
        case 'customer':
          // Redirect customers to their profile if they're on auth pages or business admin routes
          // But allow them to view public shop pages (business/:slug format)
          const isShopPage = /^\/business\/[^\/]+$/.test(currentPath); // Matches /business/slug but not /business/admin-routes
          
          if (currentPath.startsWith('/login') ||
              currentPath.startsWith('/register') ||
              (currentPath.startsWith('/business/') && !isShopPage) ||
              currentPath.startsWith('/admin/')) {
            console.log('Customer user detected, redirecting to profile');
            navigate('/profile', { replace: true });
          }
          break;
          
        case 'admin':
          // Admin users should be handled by admin login flow
          // Don't redirect admins automatically as they use separate login
          break;
          
        default:
          console.log('Unknown user role:', profile.role);
          break;
      }
    }
  }, [user, profile, isAuthenticated, isLoading, navigate]);

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return <>{children}</>;
};

export default RoleBasedRedirect;